/**
 * Tipi ed enum condivisi tra backend e frontend.
 * Rispecchiano gli enum Prisma: se cambia lo schema, cambia anche qui.
 */
export * from "./canonical-symbols.js";
export * from "./index-symbols.js";
export * from "./symbol-spec.js";
export * from "./color.js";
export * from "./prop-colors.js";
/**
 * Toglie da un testo ogni occorrenza dei segreti indicati.
 *
 * Sta in `shared` e non nel backend perche' serve in DUE posti che non possono
 * importarsi a vicenda: il **driver** (dove il messaggio del gateway nasce) e il
 * **backend** (dove i messaggi nostri finiscono in database e nelle risposte).
 * Due implementazioni della stessa regola sono la forma di difetto che questo
 * progetto ha gia' pagato: la regola sta in un posto, e i chiamanti la chiamano.
 *
 * La soglia dei tre caratteri non e' prudenza: sotto, un segreto cortissimo
 * cancellerebbe pezzi di testo a caso e renderebbe illeggibile il messaggio
 * senza nascondere niente che valga.
 */
export function redactSecrets(text, secrets) {
    return secrets.reduce((t, s) => (s && s.length >= 3 ? t.split(s).join("***") : t), text);
}
export const IPROYAL_PASSWORD_WITH_SESSION = "Password IPRoyal con _lifetime- o _streaming- ma senza _session-: incolla la stringa sticky completa dal pannello IPRoyal, oppure la password base";
export function iproyalStickyOf(provider, password) {
    if (provider?.trim().toLowerCase() !== "iproyal" || !password)
        return null;
    const param = (k) => new RegExp(`_${k}-([^_]+)`, "i").exec(password)?.[1] ?? null;
    const session = param("session");
    return session ? { session, country: param("country"), city: param("city"), lifetime: param("lifetime") } : null;
}
/**
 * Password IPRoyal con parametri di sessione MA SENZA `_session-`: rifiutata.
 *
 * La stringa sticky completa (con `_session-`) si accetta e si usa com'e'. Un
 * `_lifetime-` o `_streaming-` senza sessione invece andrebbe ricomposto con la
 * sessione del cliente in coda, e i suffissi fuori ordine il provider non li
 * accetta (26/09: «not allowed by ruleset»). Sta qui perche' la stessa regola
 * serve al backend (400 al salvataggio) e al modulo (errore in linea).
 */
export function proxyPasswordIssue(provider, password) {
    if (provider?.trim().toLowerCase() !== "iproyal" || !password)
        return null;
    if (iproyalStickyOf(provider, password))
        return null;
    return /_(lifetime|streaming)-/i.test(password) ? IPROYAL_PASSWORD_WITH_SESSION : null;
}
export const USER_ROLES = ["ADMIN", "OPERATORE", "CONTABILE", "VIEWER", "CLIENT"];
export const ACCOUNT_ROLES = ["PROP", "BROKER", "NONE"];
export const CYCLE_PHASES = ["PHASE_1", "PHASE_2", "FUNDED"];
export const CYCLE_STATES = ["ACTIVE", "CLOSED_PROFIT", "CLOSED_LOSS", "ABANDONED"];
export const SESSION_STATES = ["ACTIVE", "PAUSED", "AWAITING_PAYOUT", "AWAITING_BROKER", "COMPLETED", "FAILED"];
/**
 * PERCHE' UNA FASE E' FERMA. Obbligatorio per fermarla.
 *
 * Sta qui e non in due copie perche' la lista la usano in tre: il backend per
 * accettarla, il browser per proporla, tutt'e due per scriverla in chiaro. Due
 * liste divergono, e il giorno in cui divergessero il browser proporrebbe un
 * motivo che il backend rifiuta.
 */
export const HOLD_REASONS = ["KYC", "PAYOUT", "DEPOSITO", "CLIENTE", "ALTRO", "RESET"];
/**
 * I MOTIVI CHE SI SCELGONO OGGI (21/10): `CLIENTE` non c'e' piu', ma resta in
 * `HOLD_REASONS` perche' le Fasi ferme da prima lo portano e si leggono col
 * loro testo. Il backend accetta questi e il pannello propone questi: la
 * stessa lista, per la stessa ragione di `HOLD_REASONS`.
 */
export const HOLD_REASON_CHOICES = ["KYC", "PAYOUT", "DEPOSITO", "RESET", "ALTRO"];
/**
 * LA CAUSA DI UN FERMO, per filtrare la pagina «Fermi». E' un valore e non
 * una colonna: domani ci entrano fermi che non sono un `HoldReason` (l'attesa
 * delle credenziali), e la pagina li conta come gli altri. Un motivo che non
 * si sceglie piu' (`CLIENTE`) e una Fase ferma senza motivo vanno in `ALTRO`.
 */
export const HOLD_CAUSES = [
    "KYC",
    "PAYOUT",
    "DEPOSITO",
    "RESET",
    "ALTRO",
    // Fase chiusa (bruciata o superata) che aspetta il conto prop nuovo: una causa per fase attesa.
    "CREDENZIALI_PHASE_1",
    "CREDENZIALI_PHASE_2",
    "CREDENZIALI_FUNDED",
];
export function holdCause(reason) {
    return reason === "KYC" || reason === "PAYOUT" || reason === "DEPOSITO" || reason === "RESET" ? reason : "ALTRO";
}
/** La causa di un'attesa delle credenziali: la fase che il conto nuovo fara' nascere. */
export function awaitingPropCause(phase) {
    return `CREDENZIALI_${phase}`;
}
export const PAIR_STATES = ["PENDING", "BOTH_OPEN", "ONE_LEG", "CLOSED", "FAILED_OPEN", "ABANDONED"];
export const CLOSE_REASONS = [
    "PROP_SL_HIT",
    "PROP_TP_HIT",
    "POSITION_DD",
    "DAILY_DD_GUARD",
    "PROP_CLOSED_FIRST",
    "BROKER_CLOSED_FIRST",
    "MANUAL_CLOSE",
    "PROP_BREACH",
    "SESSION_COMPLETE",
    "DESYNC",
    "CLOSED_BY_SERVER",
];
export const DD_BASES = [
    "STATIC_INITIAL",
    "STATIC_BALANCE",
    "EOD_BALANCE",
    "TRAILING_HIGH_WATER",
    "TRAILING_LOCKED_AT_INITIAL",
];
export const RISK_EVENT_KINDS = [
    "NAKED_LEG_TIMEOUT",
    "DD_THRESHOLD",
    "DAILY_DD_THRESHOLD",
    "SESSION_FAILED",
    "ACCOUNT_DISCONNECTED",
    "PROXY_DOWN",
    "ORDER_REASON_UNEXPECTED",
    "EXECUTION_DESYNC",
    "LEVELS_DESYNC",
    "SYMBOL_SPEC_CHANGED",
    "COMMISSION_CHANGED",
    "MAX_SESSIONS_REACHED",
    "GATEWAY_LICENSE_EXPIRED",
    "MIRROR_UNREACHABLE",
    "LEG_VANISHED",
    "PAIR_CLOSED_BY_SERVER",
    "PAIR_ABANDONED",
    "BROKER_CASH_MOVE",
    "CURRENCY_MISMATCH",
    "ORDER_LANDED_LATE",
];
// ---------------------------------------------------------------------------
// Etichette in italiano per la UI. Tenute qui per non sparpagliare stringhe.
// ---------------------------------------------------------------------------
export const LABELS = {
    accountRole: {
        PROP: "Prop",
        BROKER: "Broker",
        NONE: "Nessun ruolo",
    },
    cyclePhase: {
        PHASE_1: "Fase 1",
        PHASE_2: "Fase 2",
        FUNDED: "Funded",
    },
    cycleState: {
        ACTIVE: "Attivo",
        // Il ciclo chiude alla bruciatura del prop: e' l'esito che si cerca.
        CLOSED_PROFIT: "Chiuso in profitto",
        CLOSED_LOSS: "Chiuso in perdita",
        ABANDONED: "Abbandonato",
    },
    sessionState: {
        ACTIVE: "Attiva",
        PAUSED: "In pausa",
        // Il periodo e' chiuso e il tratto nuovo non e' ancora nato: si aspetta
        // il bonifico, e finche' non arriva non si apre niente.
        AWAITING_PAYOUT: "In attesa di payout",
        // Il conto prop c'e', il broker no: nessuna apertura finche' non arriva
        // (`POST /sessions/:id/attach-broker`).
        AWAITING_BROKER: "In attesa del conto broker",
        COMPLETED: "Fase superata",
        // Nel modello Match la bruciatura e' l'esito profittevole: l'etichetta
        // non deve suggerire un errore.
        FAILED: "Conto bruciato",
    },
    holdReason: {
        KYC: "KYC",
        /*
         * «Payout» dal 21/10 (prima «Attesa bonifico»), su richiesta: lo stesso
         * valore, quindi anche le Fasi ferme da prima si leggono cosi'. Non e'
         * `AWAITING_PAYOUT` = «In attesa di payout», lo stato CONTABILE deciso
         * dalla chiusura del periodo: questo e' una decisione umana di non operare.
         */
        PAYOUT: "Payout",
        DEPOSITO: "Deposito",
        // Non si sceglie piu': resta per leggere le Fasi ferme da prima.
        CLIENTE: "Attesa del cliente",
        // Chi lo sceglie scrive che cosa aspetta: la nota li' e' obbligatoria.
        ALTRO: "Altro",
        // La prop deve ancora azzerare il conto dopo «Conto bruciato e reset».
        RESET: "Attesa reset",
    },
    /** I filtri della pagina «Fermi». */
    holdCause: {
        KYC: "KYC",
        PAYOUT: "Payout",
        DEPOSITO: "Deposito",
        RESET: "Attesa reset",
        ALTRO: "Altro",
        CREDENZIALI_PHASE_1: "Attesa credenziali Fase 1",
        CREDENZIALI_PHASE_2: "Attesa credenziali Fase 2",
        CREDENZIALI_FUNDED: "Attesa credenziali Funded",
    },
    pairState: {
        PENDING: "In apertura",
        BOTH_OPEN: "Entrambe aperte",
        ONE_LEG: "Gamba scoperta",
        CLOSED: "Chiusa",
        FAILED_OPEN: "Apertura fallita",
        // E' andata a mercato, e non sappiamo piu' dove sia finita: non e' una
        // chiusura, ed e' un'azione umana.
        ABANDONED: "Coppia abbandonata",
    },
    closeReason: {
        PROP_SL_HIT: "Stop loss sul prop",
        PROP_TP_HIT: "Take profit sul prop",
        POSITION_DD: "Budget DD della posizione esaurito",
        DAILY_DD_GUARD: "Protezione DD giornaliero",
        PROP_CLOSED_FIRST: "Prop chiuso per primo",
        BROKER_CLOSED_FIRST: "Broker chiuso per primo",
        MANUAL_CLOSE: "Chiusura manuale",
        PROP_BREACH: "Conto chiuso dalla prop firm",
        SESSION_COMPLETE: "Fase completata",
        DESYNC: "Chiusura forzata per perdita di sincronia",
        // Chiusa dal server, e il prezzo non dice se e' stato lo SL o il TP.
        CLOSED_BY_SERVER: "Chiusa dal server, motivo non deducibile",
    },
    /** Grandezze del ciclo e della sessione, come si chiamano in interfaccia. */
    terms: {
        recoverAmount: "Da recuperare",
        costsRecovered: "Costi recuperati",
        feesPaid: "Fee pagate",
        realRealizedPnl: "P&L realizzato sul reale",
        adjustments: "Rettifiche",
    },
    /**
     * I tipi di evento, detti come li direbbe una persona.
     *
     * Il messaggio dell'evento racconta il caso singolo; questa e' l'etichetta
     * della CLASSE, quella che va in un filtro e in un'intestazione di colonna.
     * `SESSION_FAILED` si chiama «Conto bruciato» come ovunque: e' l'esito
     * voluto, e l'etichetta non deve suggerire un guasto.
     */
    riskEventKind: {
        NAKED_LEG_TIMEOUT: "Gamba scoperta",
        DD_THRESHOLD: "Drawdown oltre soglia",
        DAILY_DD_THRESHOLD: "Drawdown giornaliero oltre soglia",
        SESSION_FAILED: "Conto bruciato",
        ACCOUNT_DISCONNECTED: "Conto disconnesso",
        PROXY_DOWN: "Proxy irraggiungibile",
        ORDER_REASON_UNEXPECTED: "Origine ordine inattesa",
        EXECUTION_DESYNC: "Esecuzione non allineata",
        LEVELS_DESYNC: "Livelli non allineati",
        SYMBOL_SPEC_CHANGED: "Specifiche del simbolo cambiate",
        COMMISSION_CHANGED: "Commissione cambiata",
        MAX_SESSIONS_REACHED: "Sessioni del gateway esaurite",
        GATEWAY_LICENSE_EXPIRED: "Licenza del gateway scaduta",
        MIRROR_UNREACHABLE: "Gateway irraggiungibile",
        LEG_VANISHED: "Gamba sparita da una coppia aperta",
        PAIR_CLOSED_BY_SERVER: "Coppia chiusa dal server",
        PAIR_ABANDONED: "Coppia abbandonata",
        BROKER_CASH_MOVE: "Movimento di cassa sul broker",
        CURRENCY_MISMATCH: "Valuta del conto diversa da quella del server",
        ORDER_LANDED_LATE: "Ordine arrivato a mercato in ritardo",
    },
    riskSeverity: {
        INFO: "Informativo",
        WARNING: "Avviso",
        CRITICAL: "Critico",
    },
    ddBasis: {
        STATIC_INITIAL: "Statico su saldo iniziale",
        STATIC_BALANCE: "Statico su saldo corrente",
        EOD_BALANCE: "Su saldo di fine giornata",
        TRAILING_HIGH_WATER: "Trailing sul massimo",
        TRAILING_LOCKED_AT_INITIAL: "Trailing con blocco a saldo iniziale",
    },
};
/**
 * IL MOTIVO DELLA SOSTA IN CHIARO, per un rifiuto o per una riga.
 *
 * Tre casi, e nessuno inventa niente:
 *   - nessun motivo → «motivo non registrato». Sono le Fasi fermate PRIMA che
 *     il motivo fosse obbligatorio: «non lo so» non e' «nessun motivo»;
 *   - `ALTRO` → la nota, che li' e' obbligatoria: scrivere «altro» e basta
 *     sarebbe la stessa assenza con un'altra faccia;
 *   - gli altri → l'etichetta, piu' la nota se c'e'.
 *
 * Una funzione sola per il backend e per il browser: il motivo che si legge
 * nel rifiuto dell'apertura e quello che si legge nella riga sono lo stesso
 * testo, e non c'e' un secondo posto dove possa dire un'altra cosa.
 */
export function holdText(hold) {
    if (!hold?.reason)
        return "motivo non registrato";
    if (hold.reason === "ALTRO")
        return hold.note?.trim() || LABELS.holdReason.ALTRO;
    const nota = hold.note?.trim();
    return nota ? `${LABELS.holdReason[hold.reason]} — ${nota}` : LABELS.holdReason[hold.reason];
}
export const BRANDING_ASSET_SLOTS = ["logoLight", "logoDark", "favicon"];
/** Il tetto per immagine, in byte: 500 KB i logo, 100 KB la favicon. */
export const BRANDING_ASSET_MAX_BYTES = {
    logoLight: 500 * 1024,
    logoDark: 500 * 1024,
    favicon: 100 * 1024,
};
//# sourceMappingURL=index.js.map